import React, { useState, useMemo } from "react";
import sumBy from "lodash/sumBy";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TextField from "@mui/material/TextField";
import Footer from "../pallet/Footer";
import StorageLocationAutocomplete from "../../../../views/capture/RackLocations/StorageLocationAutocomplete/StorageLocationAutocomplete";
import QuantityMismatchModal from "./QuantityMismatchModal";
import useAuth from "src/hooks/useAuth";

const styles = {
  button: {
    minWidth: 100,
  },
};

export default function Step3({
  projectId,
  onBack,
  onUpdateLocations,
  locations,
  shipmentItem,
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const [selectedLocations, setSelectedLocations] = useState([locations[0]]);
  const [quantityMismatchModalOpen, setQuantityMismatchModalOpen] = useState(false);

  const totalQty = useMemo(() => sumBy(selectedLocations, loc => loc.sub_qty || 0), [
    selectedLocations,
  ]);

  const handleSubmit = () => {
    if (shipmentItem?.quantity && totalQty !== shipmentItem.quantity) {
      setQuantityMismatchModalOpen(true);
      return;
    }

    onUpdateLocations(selectedLocations);
  };

  const handleQuantityChange = (index, value) => {
    const newLocations = [...selectedLocations];
    newLocations[index] = {
      ...newLocations[index],
      sub_qty: Number(value) || 0,
    };
    setSelectedLocations(newLocations);
  };

  return (
    <>
      <DialogContent>
        <Box textAlign="center" mb={3} minHeight={300} display="flex" alignItems="center">
          <Box width="100%">
            <Typography color="textPrimary" mb={4}>
              Which location(s) are we moving to?
            </Typography>
            <Box mt={4} display="flex" flexDirection="column" gap={1.5} mx="auto" maxWidth={500}>
              {selectedLocations.map((location, index) => (
                <Box key={index} display="flex" gap={2} alignItems="center">
                  <StorageLocationAutocomplete
                    index={index}
                    name={`location-${index}`}
                    value={location}
                    projectId={projectId}
                    onChange={values => {
                      const newLocations = [...selectedLocations];
                      newLocations[index] = { ...newLocations[index], ...values };
                      if (!newLocations[index + 1] && locations[index + 1]) {
                        newLocations.push(locations[index + 1]);
                      }
                      setSelectedLocations(newLocations);
                      if (settings.receiving_into_pallets) {
                        onUpdateLocations(newLocations);
                      }
                    }}
                    disabled={false}
                    loading={false}
                  />
                  <TextField
                    label="Location QTY"
                    type="number"
                    size="large"
                    value={location.sub_qty || ""}
                    onChange={e => handleQuantityChange(index, e.target.value)}
                    inputProps={{ min: 0 }}
                    sx={{ width: 140 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  {location.sub_qty > 0 && location.warehouse_locations?.length > 0 ? (
                    <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 34 }} />
                  ) : (
                    <Box width={34} minWidth={34} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <Footer step={2}>
        <Box />
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={onBack} sx={styles.button}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={
              selectedLocations.length !== locations.length ||
              selectedLocations.some(loc => !loc.warehouse_locations?.length || !loc.sub_qty)
            }
            sx={styles.button}
          >
            Submit
          </Button>
        </Box>
      </Footer>
      <QuantityMismatchModal
        open={quantityMismatchModalOpen}
        onClose={() => setQuantityMismatchModalOpen(false)}
        expectedQuantity={shipmentItem?.quantity || 0}
        currentQuantity={totalQty}
      />
    </>
  );
}
